<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import Twilio from './Twilio.vue';
import ThreeSixtyDialogWhatsapp from './360DialogWhatsapp.vue';
import CloudWhatsapp from './CloudWhatsapp.vue';
import WhatsappEmbeddedSignup from './WhatsappEmbeddedSignup.vue';
import GreenApiWhatsapp from './GreenApiWhatsapp.vue';
import WhatsGatewayWhatsapp from './WhatsGatewayWhatsapp.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const PROVIDER_TYPES = {
  WHATSAPP: 'whatsapp',
  TWILIO: 'twilio',
  WHATSAPP_CLOUD: 'whatsapp_cloud',
  WHATSAPP_EMBEDDED: 'whatsapp_embedded',
  WHATSAPP_MANUAL: 'whatsapp_manual',
  THREE_SIXTY_DIALOG: '360dialog',
  GREEN_API: 'green_api',
  WHATS_GATEWAY: 'whats_gateway',
};

const hasWhatsappAppId = computed(() => {
  return (
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
  );
});

const hasGreenApiEnabled = computed(() => {
  return window.chatwootConfig?.greenApiEnabled === true;
});

const selectedProvider = computed(() => route.query.provider);

const showProviderSelection = computed(() => !selectedProvider.value);

const showConfiguration = computed(() => Boolean(selectedProvider.value));

const availableProviders = computed(() => {
  const providers = [
    {
      key: PROVIDER_TYPES.WHATSAPP,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD_DESC'),
      icon: 'i-woot-whatsapp',
    },
    {
      key: PROVIDER_TYPES.TWILIO,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO_DESC'),
      icon: 'i-woot-twilio',
    },
    {
      key: PROVIDER_TYPES.THREE_SIXTY_DIALOG,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.360_DIALOG'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.360_DIALOG_DESC'),
      icon: 'i-woot-whatsapp',
    },
  ];

  // Add Green API option if enabled
  if (hasGreenApiEnabled.value) {
    providers.push({
      key: PROVIDER_TYPES.GREEN_API,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.GREEN_API'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.GREEN_API_DESC'),
      icon: 'i-woot-whatsapp',
    });
  }

  // WhatsGateway (mysim68api.com) - always available
  providers.push({
    key: PROVIDER_TYPES.WHATS_GATEWAY,
    title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATS_GATEWAY'),
    description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATS_GATEWAY_DESC'),
    icon: 'i-woot-whatsapp',
  });

  return providers;
});

const selectProvider = providerValue => {
  router.push({
    name: route.name,
    params: route.params,
    query: { provider: providerValue },
  });
};

const shouldShowCloudWhatsapp = provider => {
  return (
    provider === PROVIDER_TYPES.WHATSAPP_MANUAL ||
    (provider === PROVIDER_TYPES.WHATSAPP && !hasWhatsappAppId.value)
  );
};

const handleManualLinkClick = () => {
  selectProvider(PROVIDER_TYPES.WHATSAPP_MANUAL);
};
</script>

<template>
  <div class="wa-page overflow-auto col-span-6 p-6 w-full h-full">
    <!-- Provider selection -->
    <div v-if="showProviderSelection" class="ch-form-page">
      <div class="ch-form-page__intro">
        <h2 class="ch-form-page__title">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.TITLE') }}
        </h2>
        <p class="ch-form-page__desc">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.DESCRIPTION') }}
        </p>
      </div>
      <div class="wa-provider-grid">
        <ChannelSelector
          v-for="provider in availableProviders"
          :key="provider.key"
          :title="provider.title"
          :description="provider.description"
          :icon="provider.icon"
          @click="selectProvider(provider.key)"
        />
      </div>
    </div>

    <!-- Configuration form -->
    <div v-else-if="showConfiguration" class="wa-config-wrap">
      <div
        v-if="hasWhatsappAppId && selectedProvider === PROVIDER_TYPES.WHATSAPP"
      >
        <WhatsappEmbeddedSignup />
        <div class="wa-manual-fallback">
          <I18nT
            keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_FALLBACK"
            tag="p"
            class="wa-fallback-text"
          >
            <template #link>
              <a
                href="#"
                class="wa-fallback-link"
                @click.prevent="handleManualLinkClick"
              >
                {{
                  $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_LINK_TEXT')
                }}
              </a>
            </template>
          </I18nT>
        </div>
      </div>

      <CloudWhatsapp v-else-if="shouldShowCloudWhatsapp(selectedProvider)" />
      <Twilio
        v-else-if="selectedProvider === PROVIDER_TYPES.TWILIO"
        type="whatsapp"
      />
      <ThreeSixtyDialogWhatsapp
        v-else-if="selectedProvider === PROVIDER_TYPES.THREE_SIXTY_DIALOG"
      />
      <GreenApiWhatsapp
        v-else-if="selectedProvider === PROVIDER_TYPES.GREEN_API"
      />
      <WhatsGatewayWhatsapp
        v-else-if="selectedProvider === PROVIDER_TYPES.WHATS_GATEWAY"
      />
      <CloudWhatsapp v-else />
    </div>
  </div>
</template>

<style scoped>
.wa-page {
  background: #ffffff;
}

/* Provider selection uses ch-form-page layout */
.ch-form-page {
  max-width: 860px;
  background: #ffffff;
}
.ch-form-page__intro {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}
.ch-form-page__title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}
.ch-form-page__desc {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
}

.wa-provider-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

/* Config wrap – force white theme for sub-forms */
.wa-config-wrap {
  background: #ffffff;
}

/* Override nested tokens in sub-forms — force light theme in dark/mix mode */
.wa-config-wrap :deep(.text-n-slate-12),
.wa-config-wrap :deep(.text-slate-12),
.wa-config-wrap :deep(h1),
.wa-config-wrap :deep(h2),
.wa-config-wrap :deep(h3),
.wa-config-wrap :deep(label),
.wa-config-wrap :deep(p),
.wa-config-wrap :deep(strong) {
  color: #111827 !important;
}
.wa-config-wrap :deep(.text-n-slate-11),
.wa-config-wrap :deep(.text-slate-11) {
  color: #4b5563 !important;
}
/* Inline text elements: force dark text */
.wa-config-wrap :deep(span),
.wa-config-wrap :deep(li),
.wa-config-wrap :deep(ol),
.wa-config-wrap :deep(ul),
.wa-config-wrap :deep(code) {
  color: #111827 !important;
}

/* Force Tailwind color utilities to light mode values */
/* Blue (How to scan / info boxes) */
.wa-config-wrap :deep(.bg-blue-50) { background-color: #eff6ff !important; }
.wa-config-wrap :deep(.text-blue-600) { color: #2563eb !important; }
.wa-config-wrap :deep(.text-blue-800) { color: #1e40af !important; }
.wa-config-wrap :deep(.text-blue-900) { color: #1e3a8a !important; }
.wa-config-wrap :deep(.bg-blue-600) { background-color: #2563eb !important; }
.wa-config-wrap :deep(.border-blue-200) { border-color: #bfdbfe !important; }
/* Green (connected status) */
.wa-config-wrap :deep(.bg-green-50) { background-color: #f0fdf4 !important; }
.wa-config-wrap :deep(.text-green-600) { color: #16a34a !important; }
.wa-config-wrap :deep(.text-green-700) { color: #15803d !important; }
.wa-config-wrap :deep(.text-green-800) { color: #166534 !important; }
.wa-config-wrap :deep(.border-green-400) { border-color: #4ade80 !important; }
/* Yellow (note / waiting) */
.wa-config-wrap :deep(.bg-yellow-50) { background-color: #fefce8 !important; }
.wa-config-wrap :deep(.text-yellow-800) { color: #854d0e !important; }
.wa-config-wrap :deep(.text-yellow-900) { color: #713f12 !important; }
.wa-config-wrap :deep(.border-yellow-200) { border-color: #fde68a !important; }
/* Red (timeout / error) */
.wa-config-wrap :deep(.bg-red-50) { background-color: #fef2f2 !important; }
.wa-config-wrap :deep(.text-red-500) { color: #ef4444 !important; }
.wa-config-wrap :deep(.text-red-600) { color: #dc2626 !important; }
.wa-config-wrap :deep(.text-red-700) { color: #b91c1c !important; }
.wa-config-wrap :deep(.text-red-800) { color: #991b1b !important; }
.wa-config-wrap :deep(.border-red-200) { border-color: #fecaca !important; }
.wa-config-wrap :deep(.bg-red-100) { background-color: #fee2e2 !important; }
/* Slate (general text/borders) */
.wa-config-wrap :deep(.text-slate-300) { color: #cbd5e1 !important; }
.wa-config-wrap :deep(.border-slate-200) { border-color: #e2e8f0 !important; }
.wa-config-wrap :deep(.border-slate-300) { border-color: #cbd5e1 !important; }
/* White text stays white (for badges/icons on colored backgrounds) */
.wa-config-wrap :deep(.text-white) { color: #ffffff !important; }
/* Form inputs: force light background + dark text */
.wa-config-wrap :deep(input),
.wa-config-wrap :deep(textarea),
.wa-config-wrap :deep(select) {
  color: #111827 !important;
  background-color: #ffffff !important;
}
.wa-config-wrap :deep(input::placeholder),
.wa-config-wrap :deep(textarea::placeholder) {
  color: #9ca3af !important;
}
.wa-config-wrap :deep(.bg-n-solid-1),
.wa-config-wrap :deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
.wa-config-wrap :deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}

/* Manual fallback link */
.wa-manual-fallback {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}
.wa-fallback-text {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
}
.wa-fallback-link {
  color: #0066ff;
  text-decoration: underline;
}
</style>
